import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

interface ContextValue {
  count: number;
  register: () => () => void;
}

const ContextualOverlayContext = createContext<ContextValue | null>(null);

export function ContextualOverlayProvider({ children }: { children: ReactNode }) {
  const [count, setCount] = useState(0);

  const register = useCallback(() => {
    setCount((c) => c + 1);
    return () => setCount((c) => Math.max(0, c - 1));
  }, []);

  const value = useMemo(() => ({ count, register }), [count, register]);

  return (
    <ContextualOverlayContext.Provider value={value}>
      {children}
    </ContextualOverlayContext.Provider>
  );
}

export function useHasContextualOverlay() {
  const ctx = useContext(ContextualOverlayContext);
  return (ctx?.count ?? 0) > 0;
}

// Call from a contextual onboarding window (toast, success card, etc.)
// that shares screen real estate with the persistent feedback bubble. While
// active, the bubble hides itself. Pass `false` for components that mount
// before they should claim the corner (e.g. a toast with an internal
// "hidden" state that returns null).
export function useRegisterContextualOverlay(active: boolean = true) {
  const register = useContext(ContextualOverlayContext)?.register;
  useEffect(() => {
    if (!active) return;
    return register?.();
  }, [active, register]);
}
